import {
  exportErrorFragment,
  orderErrorFragment,
  remittanceErrorFragment
} from "@saleor/fragments/errors";
import { exportFileFragment } from "@saleor/fragments/products";
import makeMutation from "@saleor/hooks/makeMutation";
import gql from "graphql-tag";

import {
  CancelRemittance,
  CancelRemittanceVariables
} from "./types/CancelRemittance";
import {
  GenerateRemittanceId,
  GenerateRemittanceIdVariables
} from "./types/GenerateRemittanceId";
import {
  MarkRemittanceAsPaymentCollected,
  MarkRemittanceAsPaymentCollectedVariables
} from "./types/MarkRemittanceAsPaymentCollected";
import {
  RemittanceOrderExport,
  RemittanceOrderExportVariables
} from "./types/RemittanceOrderExport";
import {
  RemittanceRunnerBulkManualOverride,
  RemittanceRunnerBulkManualOverrideVariables
} from "./types/RemittanceRunnerBulkManualOverride";
import {
  RemittanceRunnerFlagOrder,
  RemittanceRunnerFlagOrderVariables
} from "./types/RemittanceRunnerFlagOrder";
import {
  RemittanceRunnerManualAdjustment,
  RemittanceRunnerManualAdjustmentVariables
} from "./types/RemittanceRunnerManualAdjustment";
import {
  RemittanceRunnerManualOverride,
  RemittanceRunnerManualOverrideVariables
} from "./types/RemittanceRunnerManualOverride";
import {
  RemittanceRunnerUploadReceipt,
  RemittanceRunnerUploadReceiptVariables
} from "./types/RemittanceRunnerUploadReceipt";

export const generateRemittanceId = gql`
  ${remittanceErrorFragment}
  mutation GenerateRemittanceId($ids: [ID]!) {
    generateRemittanceId(ids: $ids) {
      errors: remittanceErrors {
        ...RemittanceErrorFragment
      }
      remittance {
        id
        remittanceNo
        totalAmount
        createdAt
        status
        remittanceLines {
          id
          order {
            id
            orderCode
            total {
              currency
              gross {
                amount
              }
            }
          }
          createdAt
        }
      }
    }
  }
`;
export const useGenerateRemittanceId = makeMutation<
  GenerateRemittanceId,
  GenerateRemittanceIdVariables
>(generateRemittanceId);

export const cancelRemittance = gql`
  ${remittanceErrorFragment}
  mutation CancelRemittance($id: ID!) {
    remittanceCancel(id: $id) {
      errors: remittanceErrors {
        ...RemittanceErrorFragment
      }
      remittance {
        id
        status
        remittedAt
        canceledAt
      }
    }
  }
`;
export const useCancelRemittance = makeMutation<
  CancelRemittance,
  CancelRemittanceVariables
>(cancelRemittance);

export const remittanceRunnerFlagOrder = gql`
  ${orderErrorFragment}
  mutation RemittanceRunnerFlagOrder(
    $id: ID!
    $status: RemittanceManualAdjustmentStatusEnum!
  ) {
    updateRemittanceManualAdjustmentStatus(id: $id, status: $status) {
      errors: orderErrors {
        ...OrderErrorFragment
      }
      order {
        id
        orderCode
      }
    }
  }
`;
export const useRemittanceRunnerFlagOrder = makeMutation<
  RemittanceRunnerFlagOrder,
  RemittanceRunnerFlagOrderVariables
>(remittanceRunnerFlagOrder);

export const remittanceRunnerManualAdjustment = gql`
  ${orderErrorFragment}
  mutation RemittanceRunnerManualAdjustment(
    $id: ID!
    $adjustedAmount: Float!
    $adjustmentReason: String!
  ) {
    createRemittanceManualAdjustment(
      id: $id
      adjustedAmount: $adjustedAmount
      adjustmentReason: $adjustmentReason
    ) {
      errors: orderErrors {
        ...OrderErrorFragment
      }
      order {
        id
        orderCode
      }
    }
  }
`;
export const useRemittanceRunnerManualAdjustment = makeMutation<
  RemittanceRunnerManualAdjustment,
  RemittanceRunnerManualAdjustmentVariables
>(remittanceRunnerManualAdjustment);

export const remittanceRunnerUploadReceipt = gql`
  ${remittanceErrorFragment}
  mutation RemittanceRunnerUploadReceipt($id: ID!, $receiptImage: Upload!) {
    remittanceUploadReceipt(id: $id, receiptImage: $receiptImage) {
      errors: remittanceErrors {
        ...RemittanceErrorFragment
      }
      remittance {
        id
        remittanceNo
        receiptImage {
          url
          alt
        }
      }
    }
  }
`;
export const useRemittanceRunnerUploadReceipt = makeMutation<
  RemittanceRunnerUploadReceipt,
  RemittanceRunnerUploadReceiptVariables
>(remittanceRunnerUploadReceipt);

export const markRemittanceAsPaymentCollected = gql`
  ${remittanceErrorFragment}
  mutation MarkRemittanceAsPaymentCollected(
    $ids: [ID]!
    $isPaymentCollected: Boolean!
  ) {
    markRemittanceAsPaymentCollected(
      ids: $ids
      isPaymentCollected: $isPaymentCollected
    ) {
      errors: remittanceErrors {
        ...RemittanceErrorFragment
      }
      order {
        id
        orderCode
      }
    }
  }
`;
export const useMarkRemittanceAsPaymentCollected = makeMutation<
  MarkRemittanceAsPaymentCollected,
  MarkRemittanceAsPaymentCollectedVariables
>(markRemittanceAsPaymentCollected);

export const exportRemittanceOrdersMutation = gql`
  ${exportFileFragment}
  ${exportErrorFragment}
  mutation RemittanceOrderExport($input: ExportRemittanceOrdersInput!) {
    exportRemittanceOrders(input: $input) {
      exportFile {
        ...ExportFileFragment
      }
      errors: exportErrors {
        ...ExportErrorFragment
      }
    }
  }
`;

export const useExportRemittanceOrders = makeMutation<
  RemittanceOrderExport,
  RemittanceOrderExportVariables
>(exportRemittanceOrdersMutation);

export const remittanceRunnerManualOverride = gql`
  ${orderErrorFragment}
  mutation RemittanceRunnerManualOverride(
    $orderId: ID!
    $overrideStatus: RemittanceStatusEnum!
    $overrideReason: String!
    $receiptImage: Upload
  ) {
    createRemittanceManualOverride(
      orderId: $orderId
      overrideStatus: $overrideStatus
      overrideReason: $overrideReason
      receiptImage: $receiptImage
    ) {
      errors: orderErrors {
        ...OrderErrorFragment
      }
      order {
        id
        orderCode
      }
      remittance {
        id
        remittanceNo
        status
      }
    }
  }
`;
export const useRemittanceRunnerManualOverride = makeMutation<
  RemittanceRunnerManualOverride,
  RemittanceRunnerManualOverrideVariables
>(remittanceRunnerManualOverride);

export const remittanceRunnerBulkAllowManualOverride = gql`
  ${remittanceErrorFragment}
  mutation RemittanceRunnerBulkManualOverride(
    $orderIds: [ID]!
    $overrideReason: String!
    $overrideStatus: RemittanceStatusEnum!
    $receiptImage: Upload
    $isFromNonLegacy: Boolean
  ) {
    remittanceBulkAllowManualOverride(
      orderIds: $orderIds
      overrideStatus: $overrideStatus
      overrideReason: $overrideReason
      receiptImage: $receiptImage
      isFromNonLegacy: $isFromNonLegacy
    ) {
      remittances {
        id
        remittanceNo
        status
      }
      errors: remittanceErrors {
        ...RemittanceErrorFragment
      }
    }
  }
`;

export const useRemittanceBulkAllowManualOverride = makeMutation<
  RemittanceRunnerBulkManualOverride,
  RemittanceRunnerBulkManualOverrideVariables
>(remittanceRunnerBulkAllowManualOverride);
